/**
 * 清溪天成模版 - 登录页
 * 
 * 设计：居中卡片 + 装饰水波纹背景
 * 独特排版：纯居中布局，页面背景使用大尺寸水波纹动画，登录卡片带毛玻璃效果
 */

import { useState, FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function CrystalLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/";
  const { siteId } = useSite();
  const { siteConfig } = useSiteConfig();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password.trim()) {
      setError("请输入用户名和密码");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, site_id: siteId }),
      });
      const result = await response.json();
      if (result.code !== 0) {
        setError(result.message || "登录失败，请重试");
      } else {
        localStorage.setItem("token", result.data?.token || "");
        localStorage.setItem("user", JSON.stringify(result.data?.user || {}));
        navigate(redirect);
      }
    } catch (err: any) {
      setError(err?.message || "网络错误，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-gradient-to-b from-cyan-50/60 via-white to-blue-50/30">
      {/* 装饰水波纹 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full border border-cyan-200/30 animate-[spin_30s_linear_infinite]" />
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full border border-blue-200/20 animate-[spin_20s_linear_infinite_reverse]" />
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full border border-cyan-100/20 animate-[spin_25s_linear_infinite]" style={{ animationDelay: "-10s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-cyan-300/30 animate-ping" style={{ animationDuration: "3s" }} />
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 rounded-full bg-blue-400/30" />
        <div className="absolute bottom-1/4 left-1/2 w-1 h-1 rounded-full bg-cyan-400/40" />
      </div>

      {/* 登录卡片 */}
      <div className="relative z-10 w-full max-w-md mx-auto px-4">
        <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl shadow-cyan-500/10 border border-cyan-100/30 p-8 sm:p-10">
          {/* Logo */}
          <div className="text-center mb-8">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10 mx-auto mb-3" />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 mx-auto flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-cyan-400/30 mb-3">
                {siteName ? siteName.charAt(0).toUpperCase() : "C"}
              </div>
            )}
            <h1 className="text-xl font-bold text-gray-800">登录{siteName}</h1>
            <p className="text-sm text-gray-400 mt-1">欢迎回来</p>
          </div>

          {error && (
            <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center gap-2">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">用户名 / 邮箱</label>
              <div className="relative">
                <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="请输入用户名"
                  className="w-full pl-10 pr-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">密码</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full pl-10 pr-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-2xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />登录中...
                </span>
              ) : "登录"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between">
            <Link to="/" className="text-sm text-gray-400 hover:text-cyan-600 transition-colors inline-flex items-center gap-1">
              <i className="ri-arrow-left-line" />返回首页
            </Link>
            <p className="text-sm text-gray-400">
              没有账号？{" "}
              <Link to="/register" className="text-cyan-600 hover:text-cyan-700 font-semibold">注册</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
